import type { ActivityDetail, StreamsPayload } from '@painpal/contracts';
import { formatPace } from '@painpal/core';
import { useMemo, useRef } from 'react';
import { EChart } from '../../components/EChart';
import { EmptyState } from '../../components/StateViews';
import type { EChartsType } from '../../lib/echarts';
import { useVariant, type Variant } from '../../ui/useMediaQuery';
import { detailLayout } from './runDetailModel';
import {
  activateDragZoom, buildTracks, followZoomTicks, formatTrackValue, resetZoom, runTracks, STREAMS_GROUP, streamTrackOption, type TrackData,
} from './streamTracks';
import styles from './detail.module.css';

interface TrackRowProps { track: TrackData; showAxis: boolean; maxT: number; variant: Variant; onReady: (chart: EChartsType) => void }

function PaceLegend({ threshold }: { threshold: number | null | undefined }) {
  return (
    <ul className={styles.paceLegend} aria-label="Pace legend">
      <li><span className={styles.paceSwatch} data-series="pace" aria-hidden="true" />PACE</li>
      <li><span className={styles.paceSwatch} data-series="gap" aria-hidden="true" />GRADE-ADJUSTED</li>
      {typeof threshold === 'number' && <li><span className={styles.paceSwatch} data-series="threshold" aria-hidden="true" />{`THRESHOLD ${formatPace(threshold)}`}</li>}
    </ul>
  );
}

function TrackRow({ track, showAxis, maxT, variant, onReady }: TrackRowProps) {
  const option = useMemo(() => streamTrackOption(track, showAxis, maxT, variant), [track, showAxis, maxT, variant]);
  const height = (variant === 'phone' ? track.spec.phoneHeight : track.spec.height) + (showAxis ? 16 : 0);
  const pace = track.spec.kind === 'pace';
  return (
    <div className={styles.track}>
      <div className={styles.trackLabel}>
        <span className={styles.trackName} data-kind={track.spec.kind}>{track.spec.name}</span>
        <span className={styles.trackAvg}>{formatTrackValue(track.avg, track.spec)}</span>
        <span className={styles.trackNote}>{track.note}</span>
        {pace && <PaceLegend threshold={track.threshold} />}
      </div>
      <div className={styles.plot}>
        {pace && <span className={styles.paceCorner} aria-hidden="true">FASTER ↑ · SLOWER ↓</span>}
        <EChart
          option={option} height={height} label={`${track.spec.name} over time`} group={STREAMS_GROUP}
          onReady={(chart) => { onReady(chart); if (showAxis) followZoomTicks(chart); }}
        />
      </div>
    </div>
  );
}

export function StreamTracks({ streams, activity }: { streams: StreamsPayload; activity: ActivityDetail }) {
  const variant = useVariant();
  const run = detailLayout(activity) === 'run';
  const tracks = useMemo(() => (run ? runTracks(streams, activity) : buildTracks(streams)), [streams, activity, run]);
  const latest = useRef<EChartsType | null>(null);
  const onReady = (chart: EChartsType): void => {
    latest.current = chart;
    if (variant === 'desktop') activateDragZoom(chart);
  };
  const maxT = streams.t.at(-1) ?? 0;
  if (tracks.length === 0) {
    return <EmptyState title="NO SENSOR STREAMS" body="This activity has no power, heart rate, speed or elevation samples." />;
  }
  return (
    <div className={styles.tracks}>
      <div className={styles.trackTools}>
        <span className={styles.trackHint} data-part="desktop-hint">DRAG TO ZOOM · CHARTS MOVE TOGETHER</span>
        <span className={styles.trackHint} data-part="phone-hint">TOUCH &amp; HOLD TO SCRUB · PINCH TO ZOOM · TRACKS MOVE TOGETHER</span>
        <button type="button" className="btn btn-outline" onClick={() => { if (latest.current !== null) resetZoom(latest.current); }}>RESET ZOOM</button>
      </div>
      {tracks.map((track, index) => (
        <TrackRow key={track.spec.kind} track={track} showAxis={index === tracks.length - 1} maxT={maxT} variant={variant} onReady={onReady} />
      ))}
    </div>
  );
}
